import { useState } from "react";
import { X, Send, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { PresentationVideo } from "@/components/PresentationVideo";

const PLANS = [
  { value: "single_use", label: "Un informe puntual" },
  { value: "subscription_monthly", label: "Suscripción mensual" },
  { value: "subscription_annual", label: "Suscripción anual" },
  { value: "no_idea", label: "Aún no lo sé" },
];

const EMPTY = {
  name: "", pharmacy: "", phone: "", email: "", city: "",
  plan_interest: "no_idea", message: "", company_website: "",
};

const inputCls = "w-full px-3 py-2.5 bg-white text-slate-900 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block">{label}</span>
    {children}
  </label>
);

export const RequestInfoSuccess = ({ onClose }) => (
  <div className="p-8 text-center" data-testid="request-info-success">
    <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" strokeWidth={1.6} />
    <p className="font-display text-xl font-bold text-slate-900 mt-4">
      Gracias, te contactamos en menos de 24 h
    </p>
    <p className="text-sm text-slate-500 mt-2">
      Hemos recibido tus datos. Si lo prefieres, puedes llamarnos directamente al{" "}
      <a href="tel:+34609467788" className="font-mono-num font-bold text-slate-900 hover:underline">609 46 77 88</a>.
    </p>
    {onClose && (
      <button
        onClick={onClose}
        className="mt-6 bg-slate-950 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-800 transition-colors"
        data-testid="request-info-done"
      >
        Cerrar
      </button>
    )}
  </div>
);

export const RequestInfoForm = ({ source, onSent }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.pharmacy.trim()) {
      setError("Indica tu nombre y el nombre de la farmacia.");
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setError("Necesitamos un teléfono o un email para poder contactarte.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads/public", { ...form, source });
      setSent(true);
      onSent?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo enviar la solicitud. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (sent) return <RequestInfoSuccess />;

  return (
    <form onSubmit={submit} className="p-6 space-y-4" data-testid="request-info-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nombre y apellidos *">
          <input value={form.name} onChange={set("name")} className={inputCls} data-testid="ri-name" required />
        </Field>
        <Field label="Nombre de la farmacia *">
          <input value={form.pharmacy} onChange={set("pharmacy")} className={inputCls} data-testid="ri-pharmacy" required />
        </Field>
        <Field label="Teléfono">
          <input value={form.phone} onChange={set("phone")} inputMode="tel" className={inputCls} data-testid="ri-phone" />
        </Field>
        <Field label="Email">
          <input value={form.email} onChange={set("email")} type="email" className={inputCls} data-testid="ri-email" />
        </Field>
        <Field label="Población / provincia">
          <input value={form.city} onChange={set("city")} className={inputCls} data-testid="ri-city" />
        </Field>
        <Field label="¿Qué te interesa?">
          <select value={form.plan_interest} onChange={set("plan_interest")} className={inputCls} data-testid="ri-plan">
            {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Comentarios">
        <textarea
          value={form.message} onChange={set("message")} rows={3}
          placeholder="Cuéntanos qué programa de gestión usas o qué necesitas resolver"
          className={inputCls} data-testid="ri-message"
        />
      </Field>

      {/* Campo trampa anti-bots (oculto para personas) */}
      <input
        value={form.company_website} onChange={set("company_website")}
        tabIndex={-1} autoComplete="off" aria-hidden="true"
        className="absolute opacity-0 h-0 w-0 pointer-events-none"
      />

      {error && (
        <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="request-info-error">
          {error}
        </p>
      )}

      <button
        type="submit" disabled={sending}
        className="w-full bg-slate-950 text-white py-3.5 rounded-lg font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="request-info-submit"
      >
        {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</> : <><Send className="h-4 w-4" /> Enviar solicitud</>}
      </button>

      <p className="text-[11px] leading-relaxed text-slate-400">
        Responsable: Archiduque Consulting. Usaremos tus datos únicamente para contactarte y darte
        información sobre FarmaClass; no los cederemos a terceros. Puedes solicitar su supresión
        escribiendo a <a href="mailto:pedro@archiduquecarlos.com" className="underline">pedro@archiduquecarlos.com</a>.
      </p>
    </form>
  );
};

export const RequestInfoModal = ({ onClose, source = "web_login" }) => {
  const [sent, setSent] = useState(false);
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="request-info-modal"
    >
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">FarmaClass</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-950 mt-1">
              {sent ? "Solicitud enviada" : "Solicita información"}
            </h2>
            {!sent && (
              <p className="text-sm text-slate-500 mt-1">
                Cuéntanos quién eres y te explicamos cómo FarmaClass mejora tu cuota de genérico.
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 shrink-0" data-testid="request-info-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {sent ? (
          <RequestInfoSuccess onClose={onClose} />
        ) : (
          <>
            <div className="px-6 pt-5">
              <PresentationVideo testid="request-info-video" />
              <p className="text-xs text-slate-500 mt-2 text-center">
                Mira la presentación y, si quieres ver lo que hace FarmaClass, cambia de vídeo arriba.
                Déjanos tus datos justo debajo.
              </p>
            </div>
            <RequestInfoForm source={source} onSent={() => setSent(true)} />
          </>
        )}
      </div>
    </div>
  );
};

export default RequestInfoModal;
